/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { httpService } from 'src/core/services/api/httpService';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { PatternSwagger } from 'src/luis/core/services/patterns/patternSwagger';
import { PatternStore } from 'src/luis/core/stores/patternStore';
import { LuisId } from 'src/luis/types/Resource';

/**
 * Represents a service to perform CRUD operations for patterns.
 */
export const getPatternService = createService(({ context, getEnvironment }) => {
    const { apiUrl, isProduction } = getEnvironment();
    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    /**
     * Creates a common url builder scheme for this service.
     *
     * @returns A url builder string to call.
     */
    const getDefaultUrlBuilder = () =>
        urlBuilder()
            .baseUrl(apiUrl)
            .app(context.appId)
            .version(context.versionId);

    /**
     * Gets a list of patterns in the application.
     */
    const getPatterns = async (): Promise<PatternSwagger[]> => {
        const url = getDefaultUrlBuilder()
            .resource('patternRules')
            .queryParam('take', `${LuisConstants.ResourceLimits.ServiceFetchSizeLimit}`)
            .build();

        const { data: patterns } = await httpService.get<PatternSwagger[]>(url, apiOptions);

        return patterns;
    };

    /**
     * Creates a new pattern.
     *
     * @param appItem The pattern to be created.
     * @returns A promise of the newly created pattern swagger.
     */
    const createPattern = async ({ text, intentName }: Pick<PatternStore, 'text'> & { intentName: string }): Promise<PatternSwagger> => {
        const url = getDefaultUrlBuilder()
            .resource('patternRule')
            .build();

        const { data: pattern } = await httpService.post<PatternSwagger>(url, { pattern: text, intent: intentName }, apiOptions);

        return pattern;
    };

    /**
     * Deletes the patterns with the given ids.
     */
    const deletePatterns = async (ids: LuisId[]): Promise<void> => {
        const url = getDefaultUrlBuilder()
            .resource('patternRules')
            .build();

        await httpService.delete<string[]>(url, { ...apiOptions, data: ids });
    };

    /**
     * Updates pattern information by id.
     *
     * @param param0.id The id of the pattern to be updated.
     * @param param0.text The new text of the pattern.
     * @param param0.intentName The new labeled intent name of the pattern.
     */
    const updatePatterns = async (patterns: (Pick<PatternStore, 'id' | 'text'> & { intentName: string })[]): Promise<void> => {
        const url = getDefaultUrlBuilder()
            .resource('patternRules')
            .build();

        await httpService.put<PatternSwagger>(
            url,
            patterns.map(pattern => ({ id: pattern.id, pattern: pattern.text, intent: pattern.intentName })),
            apiOptions
        );
    };

    return { getPatterns, createPattern, deletePatterns, updatePatterns };
});
